import * as Sentry from "@sentry/react";
import { useEffect } from "react";
// Assuming Link might be needed from react-router-dom, but problem uses <a> for simplicity
// import { Link } from 'react-router-dom'; 

interface ErrorScreenProps {
  message?: string; // Made message optional to align with example, providing a default
  // onRetry?: () => void; // The original component has handleReload, which acts as onRetry
}

export function ErrorScreen({ message = "An unexpected error occurred." }: ErrorScreenProps) {
  // Handle special messages
  const isTelegramError = message === "Authentication required" || 
                          message === "Please open this app through Telegram";
  
  // Report error to Sentry when component mounts
  useEffect(() => {
    if (!isTelegramError) {
      Sentry.captureMessage(`Error screen displayed: ${message}`, {
        level: "error",
      });
    }
  }, [message, isTelegramError]);
  
  const handleReload = () => {
    window.location.reload();
  };
  
  const displayedMessage = isTelegramError 
    ? "This app must be opened through the Telegram app. Please use the t.me link provided."
    : message;

  const headline = isTelegramError ? "Telegram Connection Required" : "Oops! Something went wrong.";

  return (
    <div className="flex flex-col justify-center items-center h-screen bg-gray-50 dark:bg-gray-900 text-center p-6">
      <div className="mb-6">
        <span 
          className="material-symbols-outlined text-7xl text-red-500 dark:text-red-400 animate-subtleWobble"
        >
          {isTelegramError ? 'phonelink_erase' : 'error_outline'}
        </span>
      </div>
      <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-3">
        {headline}
      </h1>
      <p className="text-md text-gray-600 dark:text-gray-400 mb-6 max-w-md">
        {displayedMessage}
      </p>
      
      {isTelegramError ? (
        <div className="flex flex-col items-center space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-300">
            If you're using Telegram desktop, try opening this link on your mobile device instead.
          </p>
          {/* You could add a more visually appealing element here if desired */}
          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
             <span className="material-symbols-outlined text-3xl text-blue-500 dark:text-blue-400 animate-pulse">
                tap_and_play
             </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
          <a 
            href="/" 
            className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-colors duration-150"
          >
            Go Home
          </a>
          <button 
            onClick={handleReload}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg shadow-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-900 transition-colors duration-150"
          >
            Try Again
          </button>
        </div>
      )}
      
      {!isTelegramError && (
         <p className="mt-8 text-xs text-gray-500 dark:text-gray-400">
           Error details: {message}
         </p>
      )}
    </div>
  );
}